/**
 * Footer Amp Carousel
 * Scroll-linked horizontal animation for amp images
 *
 * @package Commcore26
 */

export const AmpCarousel = {
	carousel: null,
	track: null,
	isInView: false,
	scrollMultiplier: 1.5, // scrollMultiplier 2 would be 50px scroll = 100px movement
	direction: 'right', // 'left' or 'right'
	setWidth: 0, // Width of one set of images

	init() {
		// Respect reduced motion preference
		if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
			return;
		}

		this.carousel = document.querySelector('.footer-amp-carousel');

		if (!this.carousel) {
			return;
		}

		this.wrapImages();
		this.setupObserver();
		this.bindEvents();
	},

	/**
	 * Wrap images in a track element and clone for infinite loop
	 */
	wrapImages() {
		const images = Array.from(this.carousel.querySelectorAll('img'));

		// Create track wrapper
		this.track = document.createElement('div');
		this.track.className = 'footer-amp-carousel__track';

		// Move original images into track
		images.forEach(img => {
			this.track.appendChild(img);
		});

		this.carousel.appendChild(this.track);

		// Calculate width of one set after images are in DOM
		// Use setTimeout to ensure layout is complete
		setTimeout(() => {
			this.setWidth = this.calculateSetWidth();
			this.cloneImages(images);
		}, 0);
	},

	/**
	 * Calculate the width of one set of images including gaps
	 */
	calculateSetWidth() {
		const trackStyle = getComputedStyle(this.track);
		const gap = parseFloat(trackStyle.gap) || 0;
		const images = this.track.querySelectorAll('img:not(.clone)');
		let width = 0;

		images.forEach(img => {
			width += img.offsetWidth + gap;
		});

		return width;
	},

	/**
	 * Clone images to create seamless infinite loop
	 */
	cloneImages(images) {
		// Clone images multiple times to ensure coverage during scroll
		const clonesNeeded = 3;

		for (let i = 0; i < clonesNeeded; i++) {
			images.forEach(img => {
				const clone = img.cloneNode(true);
				clone.classList.add('clone');
				this.track.appendChild(clone);
			});
		}

		// Set initial position for 'right' direction
		if (this.direction === 'right') {
			this.track.style.transform = `translateX(-${this.setWidth}px)`;
		}
	},

	/**
	 * Set up IntersectionObserver to detect when carousel is in viewport
	 */
	setupObserver() {
		const observer = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					this.isInView = entry.isIntersecting;
				});
			},
			{
				threshold: 0,
				rootMargin: '0px',
			}
		);

		observer.observe(this.carousel);
	},

	/**
	 * Bind scroll events
	 */
	bindEvents() {
		let ticking = false;

		window.addEventListener('scroll', () => {
			if (!ticking && this.isInView) {
				window.requestAnimationFrame(() => {
					this.updatePosition();
					ticking = false;
				});
				ticking = true;
			}
		}, { passive: true });
	},

	/**
	 * Update track position based on scroll
	 */
	updatePosition() {
		if (!this.isInView || !this.track || !this.setWidth) {
			return;
		}

		const rect = this.carousel.getBoundingClientRect();
		const viewportHeight = window.innerHeight;

		// Calculate how far into the viewport the element has scrolled
		// When element top is at viewport bottom, progress = 0
		// When element top is at viewport top, progress = viewportHeight
		const scrollProgress = viewportHeight - rect.top;

		// Only animate when the element is actually visible
		if (scrollProgress > 0) {
			// Use modulo to create infinite loop effect
			const progress = (scrollProgress * this.scrollMultiplier) % this.setWidth;

			let translateX;
			if (this.direction === 'right') {
				// Start at -setWidth and move toward 0
				translateX = -this.setWidth + progress;
			} else {
				// Start at 0 and move toward -setWidth
				translateX = -progress;
			}

			this.track.style.transform = `translateX(${translateX}px)`;
		}
	},
};
